/* eslint-disable no-console */
import {Col, Form, Input, Select} from 'antd';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import React from 'react';
import {useSearchParams} from 'react-router-dom';

import {ReactComponent as YearCalender} from '../../assets/yearCalender.svg';
import {CommonErrors} from '../../constants/Common';
import {useAIDocumentOptions} from '../../hooks/useAIDocumentOptions';
import {financialYear} from '../../interface/Units';
import {yearSelect} from '../../utils/FormUtils/yearSelect';
import DocumentUploadPreview from '../DocumentUploadPreview/DocumentUploadPreview';

import {
  AIDocumentManagerKey,
  AIDocumentManagerLabel
} from './Constants/IP/AIDocumentManagerLabel';
import {yearKeyLabel} from './Constants/IP/commonKeyLabel';

const ESGDocumentManagerForm: React.FC<{
  currentStep?: number;
  isEdit?: boolean;
}> = ({currentStep, isEdit = false}) => {
  const FormInstance = useFormInstance();
  const {options, frameworkOptions, documentTypeOptions} =
    useAIDocumentOptions();

  const yearFilter = yearSelect(financialYear.AprMar);
  const [searchParam] = useSearchParams();

  const docTypeFromParam = searchParam.get('frameworkDocumentType');
  const initialDocType =
    docTypeFromParam && docTypeFromParam !== 'all'
      ? docTypeFromParam
      : undefined;

  const extensions = options?.extensions?.map((ext) =>
    ext.replace(/^\./, '').toLowerCase()
  );
  const maxMB = options?.max_mb;

  return (
    <>
      {currentStep === 0 && (
        <>
          {/* YEAR */}
          <Col className="gutter-row">
            <Form.Item
              name={AIDocumentManagerKey.YEAR}
              label={AIDocumentManagerLabel.YEAR}
              labelCol={{span: 24}}
              rules={[
                {
                  required: true,
                  message: CommonErrors.mandatory(AIDocumentManagerLabel.YEAR)
                }
              ]}
              initialValue={searchParam.get('selectedYear')}
            >
              <Select
                placeholder={yearKeyLabel.label}
                options={yearFilter}
                suffixIcon={<YearCalender />}
              />
            </Form.Item>
          </Col>

          {/* FRAMEWORK */}
          <Col className="gutter-row">
            <Form.Item
              name={AIDocumentManagerKey.BILL_TYPE}
              label={AIDocumentManagerLabel.FRAMEWORK}
              labelCol={{span: 24}}
              initialValue={'BRSR'}
              rules={[
                {
                  required: true,
                  message: CommonErrors.mandatory(
                    AIDocumentManagerLabel.FRAMEWORK
                  )
                }
              ]}
              validateFirst
            >
              <Select
                className="select"
                placeholder={`Choose ${AIDocumentManagerLabel.FRAMEWORK}`}
                options={frameworkOptions}
                disabled={true}
                onChange={() => {
                  // Reset document type + validation
                  FormInstance.setFields([
                    {
                      name: AIDocumentManagerKey.DOCUMENT_TYPE,
                      value: undefined
                    }
                  ]);
                }}
              />
            </Form.Item>
          </Col>

          {/* DOCUMENT TYPE */}
          <Col className="gutter-row">
            <Form.Item
              shouldUpdate={(prev, curr) =>
                prev[AIDocumentManagerKey.BILL_TYPE] !==
                curr[AIDocumentManagerKey.BILL_TYPE]
              }
              noStyle
            >
              {({getFieldValue}) => {
                const framework = getFieldValue(AIDocumentManagerKey.BILL_TYPE);

                const docTypes = documentTypeOptions(framework);

                return (
                  <Form.Item
                    name={AIDocumentManagerKey.DOCUMENT_TYPE}
                    label={AIDocumentManagerLabel.DOCUMENT_TYPE}
                    labelCol={{span: 24}}
                    initialValue={initialDocType}
                    rules={[
                      {
                        required: true,
                        message: CommonErrors.mandatory(
                          AIDocumentManagerLabel.DOCUMENT_TYPE
                        )
                      }
                    ]}
                  >
                    <Select
                      className="select"
                      placeholder={`Choose ${AIDocumentManagerLabel.DOCUMENT_TYPE}`}
                      options={docTypes}
                      disabled={!framework}
                      showSearch={true}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>

            <Form.Item name={'type'} initialValue={'ESG'} hidden />
          </Col>

          {isEdit && (
            <Col className="gutter-row">
              <Form.Item
                name="file_name"
                label="File Name"
                labelCol={{span: 24}}
                rules={[
                  {
                    required: true,
                    message: CommonErrors.mandatory('File Name')
                  }
                ]}
              >
                <Input placeholder="File name" />
              </Form.Item>
            </Col>
          )}
        </>
      )}

      {/* STEP 2 */}
      {currentStep === 1 && (
        <>
          <Col className="gutter-row">
            <Form.Item noStyle shouldUpdate>
              <DocumentUploadPreview
                formInstance={FormInstance}
                validationMessage="Please upload document"
                allowedExtensions={extensions}
                maxSizeMB={maxMB}
                keepFile
                typeMessage={
                  extensions
                    ? `Only ${extensions
                        .join(', ')
                        .toUpperCase()} files allowed`
                    : undefined
                }
                sizeMessage={
                  maxMB ? `File must be smaller than ${maxMB}MB` : undefined
                }
              />
            </Form.Item>
            <Form.Item name="file_name" hidden />
          </Col>
        </>
      )}
    </>
  );
};

export default ESGDocumentManagerForm;
